// 自訂總覽:釘選(星標→置頂)+ 排序(APP#27,HA Lovelace 對標克制版)。
//
// v1=本地偏好(localStorage,per-uid key)——與房間分組(rooms.ts)、tile 排序
// 同一個 §9-G3「本地排序偏好」模式;伺服端同步(跨裝置)列缺口,落地時走
// user-prefs additive 面、不動 devices 契約。釘選/排序皆純呈現層概念,
// 平台規格不動。釘選恆浮在所選排序之上,並與房間分組組合(各組內套用)。

const PIN_KEY = (uid: string) => `lsdg.pins.${uid || 'anon'}`;
const SORT_KEY = (uid: string) => `lsdg.devsort.${uid || 'anon'}`;
// boot 競速:silent refresh 後 user 物件晚到(uid 短暫為 '')——last-written
// 鏡像讓 reload 不丟偏好;uid 到位後讀回同一份(同 rooms.ts 模式)。
const PIN_LAST = 'lsdg.pins.last';
const SORT_LAST = 'lsdg.devsort.last';

export type SortMode = 'default' | 'name' | 'status' | 'recent' | 'custom';
export const SORT_MODES: SortMode[] = ['default', 'name', 'status', 'recent', 'custom'];

function isSortMode(v: unknown): v is SortMode {
  return typeof v === 'string' && (SORT_MODES as string[]).includes(v);
}

/** 載入釘選清單(device_id 陣列;空=無釘選)。 */
export function loadPins(uid: string): string[] {
  try {
    const raw = localStorage.getItem(PIN_KEY(uid)) ?? localStorage.getItem(PIN_LAST);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** 切換釘選;回傳更新後清單。 */
export function togglePin(uid: string, deviceId: string): string[] {
  const cur = loadPins(uid);
  const next = cur.includes(deviceId) ? cur.filter((id) => id !== deviceId) : [...cur, deviceId];
  try {
    const enc = JSON.stringify(next);
    localStorage.setItem(PIN_KEY(uid), enc);
    localStorage.setItem(PIN_LAST, enc);
  } catch {
    /* quota/private mode — degrade to in-memory for this session */
  }
  return next;
}

/** 載入排序模式(預設 default)。 */
export function loadSort(uid: string): SortMode {
  try {
    const raw = localStorage.getItem(SORT_KEY(uid)) ?? localStorage.getItem(SORT_LAST);
    return isSortMode(raw) ? raw : 'default';
  } catch {
    return 'default';
  }
}

/** 儲存排序模式;回傳已存值。 */
export function saveSort(uid: string, mode: SortMode): SortMode {
  const m: SortMode = isSortMode(mode) ? mode : 'default';
  try {
    localStorage.setItem(SORT_KEY(uid), m);
    localStorage.setItem(SORT_LAST, m);
  } catch {
    /* degrade to in-memory */
  }
  return m;
}

type Sortable = {
  device_id: string;
  aliasname?: string;
  display_name?: string;
  model_name?: string;
  online?: { state?: string };
  last_event?: { event_ts?: number };
};

// 與 deviceModel.deviceName() 同優先序(別名→裝置名→機型→id)。
function nameKey(d: Sortable): string {
  return (d.aliasname || d.display_name || d.model_name || d.device_id || '').toString();
}

/**
 * 釘選 + 排序組合(純函式,不變更輸入)。
 * 1. 釘選的恆排在最前(依原清單相對順序);
 * 2. 其餘依 mode:
 *    - default：維持輸入順序(平台/房間既有順序)
 *    - name：顯示名 localeCompare(zh-Hant)
 *    - status：online 先、offline 後(同態維持原序)
 *    - recent：最近事件時間新→舊(無事件殿後)
 *    - custom：依 `order`(拖曳結果)索引;不在 order 者(新裝置)殿後維持原序
 * 穩定排序:同鍵維持原相對順序。
 */
export function sortDevices<T extends Sortable>(devices: T[], pins: string[], mode: SortMode, order: string[] = []): T[] {
  const pinSet = new Set(pins);
  const idx = new Map(devices.map((d, i) => [d.device_id, i] as const));
  const stable = (a: T, b: T) => (idx.get(a.device_id)! - idx.get(b.device_id)!);
  const orderIdx = new Map(order.map((id, i) => [id, i] as const));

  const cmp = (a: T, b: T): number => {
    const ap = pinSet.has(a.device_id) ? 0 : 1;
    const bp = pinSet.has(b.device_id) ? 0 : 1;
    if (ap !== bp) return ap - bp; // 釘選優先
    switch (mode) {
      case 'name':
        return nameKey(a).localeCompare(nameKey(b), 'zh-Hant') || stable(a, b);
      case 'status': {
        const ao = a.online?.state === 'online' ? 0 : 1;
        const bo = b.online?.state === 'online' ? 0 : 1;
        return ao - bo || stable(a, b);
      }
      case 'recent': {
        const at = a.last_event?.event_ts ?? -1;
        const bt = b.last_event?.event_ts ?? -1;
        return bt - at || stable(a, b);
      }
      case 'custom': {
        const ao = orderIdx.get(a.device_id) ?? Infinity;
        const bo = orderIdx.get(b.device_id) ?? Infinity;
        return ao - bo || stable(a, b);
      }
      default:
        return stable(a, b);
    }
  };
  return [...devices].sort(cmp);
}

export function isPinned(pins: string[], deviceId: string): boolean {
  return pins.includes(deviceId);
}

// ---- 卡片密度（APP#51,VistaFlow 卡片尺寸切換對標）----
// 同 §9-G3 本地偏好模式(per-uid);純呈現層,平台契約不動。
export type Density = 'comfortable' | 'compact';
const DENSITY_KEY = (uid: string) => `lsdg.density.${uid || 'anon'}`;
const DENSITY_LAST = 'lsdg.density.last';

export function loadDensity(uid: string): Density {
  try {
    const raw = localStorage.getItem(DENSITY_KEY(uid)) ?? localStorage.getItem(DENSITY_LAST);
    return raw === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

export function saveDensity(uid: string, d: Density): Density {
  const v: Density = d === 'compact' ? 'compact' : 'comfortable';
  try {
    localStorage.setItem(DENSITY_KEY(uid), v);
    localStorage.setItem(DENSITY_LAST, v);
  } catch {
    /* degrade to in-memory */
  }
  return v;
}

// ---- 手動排序（APP#51 拖曳;VistaFlow 長按拖曳對標）----
// 全域裝置順序(device_id 陣列);於各房間分組內以 sortDevices('custom', order) 套用。
const ORDER_KEY = (uid: string) => `lsdg.devorder.${uid || 'anon'}`;
const ORDER_LAST = 'lsdg.devorder.last';

export function loadOrder(uid: string): string[] {
  try {
    const raw = localStorage.getItem(ORDER_KEY(uid)) ?? localStorage.getItem(ORDER_LAST);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function saveOrder(uid: string, order: string[]): string[] {
  try {
    const enc = JSON.stringify(order);
    localStorage.setItem(ORDER_KEY(uid), enc);
    localStorage.setItem(ORDER_LAST, enc);
  } catch {
    /* degrade to in-memory */
  }
  return order;
}

/**
 * 把 fromId 移動到 toId 之前，回新陣列（純函式，不變更輸入）。
 * - fromId===toId → 原樣;
 * - 若 order 尚未含某端(拖到未追蹤裝置)→ 先以 `present` 補全再移，確保新裝置也可排;
 * - toId 不在補全後清單 → 不動。
 */
export function moveInOrder(order: string[], fromId: string, toId: string, present: string[] = []): string[] {
  if (fromId === toId) return [...order];
  // 以 present(目前顯示的全部裝置,原序)補全 order 中缺的項,讓新裝置可被拖動/當落點。
  const merged = [...order];
  for (const id of present) if (!merged.includes(id)) merged.push(id);
  const arr = merged.filter((id) => id !== fromId);
  const idx = arr.indexOf(toId);
  if (idx < 0) return merged;
  arr.splice(idx, 0, fromId);
  return arr;
}
